import { useEffect, useMemo, useState } from 'react'
import { Alert } from '../../components/atoms/Alert'
import { Button } from '../../components/atoms/Button'
import { Input } from '../../components/atoms/Input'
import { Modal } from '../../components/atoms/Modal'
import { Select } from '../../components/atoms/Select'
import { Spinner } from '../../components/atoms/Spinner'
import { Textarea } from '../../components/atoms/Textarea'
import { Field } from '../../components/molecules/Field'
import { compressImageIfApplicable } from '../../../utils/image-compress'
import type {
  BillDownload,
  BillUploadInput,
  CreateManagePurchaseInput,
  ManagePurchaseDto,
  UnitDto,
  UpdateManagePurchaseInput
} from '../../../viewmodels/useManagePurchases'

const MAX_PAISA = 100_00_00_00_000
const MAX_QTY_MILLIS = 10_000_000_000
const NOTES_MAX = 2048
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/
const BILL_MAX_BYTES = 10 * 1024 * 1024
const BILL_ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf']

interface SupplierRef {
  readonly id: string
  readonly name: string
}

interface Props {
  readonly open: boolean
  readonly mode: 'create' | 'edit'
  readonly initial: ManagePurchaseDto | null
  /** Current GST split — CGST + SGST in basis points (900 + 900 = 18%
   *  total). Both null if the operator hasn't set one — form shows a
   *  warning + disables save. */
  readonly currentGstCgstBps: number | null
  readonly currentGstSgstBps: number | null
  /** Active units catalogue for the unit dropdown. */
  readonly units: readonly UnitDto[]
  /** Active suppliers catalogue for the supplier dropdown (all types). */
  readonly suppliers: readonly SupplierRef[]
  readonly onClose: () => void
  readonly onSubmit: (
    input: CreateManagePurchaseInput | UpdateManagePurchaseInput
  ) => Promise<{ ok: true } | { ok: false; field?: string | null; message: string }>
  readonly getBill?: (id: string) => Promise<{ ok: true; data: BillDownload } | { ok: false; message: string }>
  readonly uploadBill?: (
    id: string,
    input: BillUploadInput
  ) => Promise<{ ok: true; data: ManagePurchaseDto } | { ok: false; field: string | null; message: string }>
  readonly deleteBill?: (id: string) => Promise<{ ok: true; data: ManagePurchaseDto } | { ok: false; message: string }>
}

interface FormState {
  invoiceNo: string
  supplierId: string
  qtyText: string
  unitId: string
  /** User enters the invoice total (incl. GST). Pre-GST + GST are
   *  computed client-side from currentGstRateBps and shown below. */
  invoiceTotalText: string
  purchaseDate: string
  billedDate: string
  notes: string
}

const todayISO = (): string => new Date().toISOString().slice(0, 10)
const INVOICE_NO_MAX = 64

const EMPTY: FormState = {
  invoiceNo: '',
  supplierId: '',
  qtyText: '',
  unitId: '',
  invoiceTotalText: '',
  purchaseDate: '',
  billedDate: '',
  notes: ''
}

/**
 * Split an invoice total (paisa, incl. GST) into pre-GST + CGST + SGST
 * using the current rate components. GST-inclusive math against the
 * COMBINED rate keeps the rounding consistent with the legacy single-
 * rate computation; the cgst/sgst split then divides the resulting GST
 * paisa proportionally between the two components (odd paisa lands on
 * SGST so cgst + sgst = total GST exactly — no rounding loss).
 */
function splitInclusive(
  totalPaisa: number,
  cgstBps: number,
  sgstBps: number
): { preGstPaisa: number; cgstPaisa: number; sgstPaisa: number } {
  const rateBps = cgstBps + sgstBps
  const gstPaisa = rateBps === 0 ? 0 : Math.round((totalPaisa * rateBps) / (10000 + rateBps))
  const cgstPaisa = rateBps === 0 ? 0 : Math.floor((gstPaisa * cgstBps) / rateBps)
  const sgstPaisa = gstPaisa - cgstPaisa
  return { preGstPaisa: totalPaisa - gstPaisa, cgstPaisa, sgstPaisa }
}

function paisaToRupeeString(p: number): string {
  return (p / 100).toFixed(2)
}
function millisToText(m: number): string {
  return (m / 1000).toString()
}
function parseRupees(raw: string, label: string): number {
  const t = raw.trim()
  if (t === '') throw new Error(`${label} is required`)
  const n = Number(t)
  if (!Number.isFinite(n) || n < 0) throw new Error(`${label} must be >= 0`)
  const p = Math.round(n * 100)
  if (p > MAX_PAISA) throw new Error(`${label} out of range`)
  return p
}
function parseQty(raw: string): number {
  const t = raw.trim()
  if (t === '') throw new Error('Quantity is required')
  const n = Number(t)
  if (!Number.isFinite(n) || n <= 0) throw new Error('Quantity must be > 0')
  const m = Math.round(n * 1000)
  if (m > MAX_QTY_MILLIS) throw new Error('Quantity out of range')
  return m
}

/**
 * ManagePurchaseFormModal — bare purchase add/edit. No pickers; just
 * the header fields + a file-browse bill upload (edit mode).
 */
export function ManagePurchaseFormModal({
  open,
  mode,
  initial,
  currentGstCgstBps,
  currentGstSgstBps,
  units,
  suppliers,
  onClose,
  onSubmit,
  getBill,
  uploadBill,
  deleteBill
}: Props): JSX.Element {
  const initialFromRow: FormState = useMemo(() => {
    if (initial === null) {
      return { ...EMPTY, purchaseDate: todayISO(), billedDate: todayISO() }
    }
    // Edit mode: total_paid is the invoice total (GST-inclusive in our
    // schema). Show it back as the invoice total; the split re-renders
    // from the current rate.
    return {
      invoiceNo: initial.invoice_no ?? '',
      supplierId: initial.supplier_id ?? '',
      qtyText: millisToText(initial.quantity_millis),
      unitId: initial.unit_id ?? '',
      invoiceTotalText: paisaToRupeeString(initial.total_paid_paisa),
      purchaseDate: initial.purchase_date,
      billedDate: initial.billed_date,
      notes: initial.notes ?? ''
    }
  }, [initial])

  // Unit options = catalog + the row's existing unit if it's been
  // soft-deleted from the catalog (so the operator sees it as
  // "(removed) <name>" instead of an empty dropdown on edit).
  const unitOptions = useMemo<UnitDto[]>(() => {
    const editingId = initial?.unit_id ?? null
    if (editingId !== null && !units.some((u) => u.id === editingId)) {
      const editingLabel = initial?.unit_label ?? '(removed unit)'
      return [{ id: editingId, name: `(removed) ${editingLabel}` }, ...units]
    }
    return [...units]
  }, [units, initial])

  // Supplier options follow the same "(removed)" pattern so an edited
  // row whose supplier has since been soft-deleted still resolves to
  // a visible option instead of an empty dropdown.
  const supplierOptions = useMemo<SupplierRef[]>(() => {
    const editingId = initial?.supplier_id ?? null
    if (editingId !== null && !suppliers.some((s) => s.id === editingId)) {
      const editingName = initial?.supplier_name ?? '(removed supplier)'
      return [{ id: editingId, name: `(removed) ${editingName}` }, ...suppliers]
    }
    return [...suppliers]
  }, [suppliers, initial])

  const [state, setState] = useState<FormState>(EMPTY)
  const [submitting, setSubmitting] = useState(false)
  const [fieldError, setFieldError] = useState<{ field: keyof FormState; message: string } | null>(null)
  const [formError, setFormError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      // Default unitId to the first available unit on Add (when
      // it would otherwise be blank).
      const seeded: FormState =
        initial === null && initialFromRow.unitId === ''
          ? { ...initialFromRow, unitId: units[0]?.id ?? '' }
          : initialFromRow
      setState(seeded)
      setFieldError(null)
      setFormError(null)
      setSubmitting(false)
    }
  }, [open, initialFromRow, initial, units])

  const handleSubmit = async (): Promise<void> => {
    setFieldError(null)
    setFormError(null)
    const invoiceNoTrimmed = state.invoiceNo.trim()
    if (invoiceNoTrimmed.length < 1 || invoiceNoTrimmed.length > INVOICE_NO_MAX) {
      setFieldError({ field: 'invoiceNo', message: `Invoice number must be 1..${INVOICE_NO_MAX} chars` })
      return
    }
    if (state.supplierId === '') {
      setFieldError({ field: 'supplierId', message: 'Supplier is required' })
      return
    }
    if (state.unitId === '') {
      setFieldError({ field: 'unitId', message: 'Unit is required' })
      return
    }
    if (!DATE_PATTERN.test(state.purchaseDate)) {
      setFieldError({ field: 'purchaseDate', message: 'Pick a valid date' })
      return
    }
    if (!DATE_PATTERN.test(state.billedDate)) {
      setFieldError({ field: 'billedDate', message: 'Pick a valid invoice date' })
      return
    }
    if (state.notes.length > NOTES_MAX) {
      setFieldError({ field: 'notes', message: `notes must be <=${NOTES_MAX} chars` })
      return
    }
    if (currentGstCgstBps === null || currentGstSgstBps === null) {
      setFormError('No current GST rate set. Add one on Manage > GST Rates first.')
      return
    }
    let qty: number
    let total: number
    try { qty = parseQty(state.qtyText) }
    catch (e) { setFieldError({ field: 'qtyText', message: msg(e) }); return }
    try { total = parseRupees(state.invoiceTotalText, 'Invoice total') }
    catch (e) { setFieldError({ field: 'invoiceTotalText', message: msg(e) }); return }
    const { cgstPaisa, sgstPaisa } = splitInclusive(total, currentGstCgstBps, currentGstSgstBps)

    setSubmitting(true)
    const payload = {
      invoice_no: invoiceNoTrimmed,
      supplier_id: state.supplierId,
      quantity_millis: qty,
      unit_id: state.unitId,
      total_paid_paisa: total,
      cgst_amount_paisa: cgstPaisa,
      sgst_amount_paisa: sgstPaisa,
      purchase_date: state.purchaseDate,
      billed_date: state.billedDate,
      notes: state.notes === '' ? null : state.notes
    }
    const result = await onSubmit(payload)
    setSubmitting(false)
    if (result.ok) {
      onClose()
      return
    }
    const map: Record<string, keyof FormState> = {
      invoice_no: 'invoiceNo',
      supplier_id: 'supplierId',
      quantity_millis: 'qtyText',
      unit_id: 'unitId',
      total_paid_paisa: 'invoiceTotalText',
      cgst_amount_paisa: 'invoiceTotalText',
      sgst_amount_paisa: 'invoiceTotalText',
      purchase_date: 'purchaseDate',
      billed_date: 'billedDate',
      notes: 'notes'
    }
    const f = result.field !== null && result.field !== undefined ? map[result.field] : undefined
    if (f !== undefined) setFieldError({ field: f, message: result.message })
    else setFormError(result.message)
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={mode === 'create' ? 'Add purchase' : 'Edit purchase'}
      maxWidth="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button onClick={() => void handleSubmit()} disabled={submitting}>
            {submitting ? 'Saving…' : mode === 'create' ? 'Add' : 'Save'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {formError !== null ? <Alert tone="error">{formError}</Alert> : null}

        <Field
          label="Invoice / Bill number"
          helper="Required. Must be unique within Manage Purchases (case-insensitive)."
          error={fieldError?.field === 'invoiceNo' ? fieldError.message : null}
        >
          <Input
            value={state.invoiceNo}
            hasError={fieldError?.field === 'invoiceNo'}
            onChange={(e) => setState((s) => ({ ...s, invoiceNo: e.target.value }))}
            placeholder="e.g. INV-2026-0042"
            maxLength={INVOICE_NO_MAX}
          />
        </Field>

        <Field label="Supplier" error={fieldError?.field === 'supplierId' ? fieldError.message : null}>
          <Select
            value={state.supplierId}
            hasError={fieldError?.field === 'supplierId'}
            onChange={(e) => setState((s) => ({ ...s, supplierId: e.target.value }))}
            aria-label="Supplier"
          >
            <option value="">— Select a supplier —</option>
            {supplierOptions.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </Select>
        </Field>

        <div className="grid grid-cols-2 gap-4">
          <Field
            label={(() => {
              const u = units.find((u) => u.id === state.unitId)
              return `Quantity (${u?.name ?? 'unit'})`
            })()}
            error={fieldError?.field === 'qtyText' ? fieldError.message : null}
          >
            <Input
              value={state.qtyText}
              hasError={fieldError?.field === 'qtyText'}
              onChange={(e) => setState((s) => ({ ...s, qtyText: e.target.value }))}
              placeholder="e.g. 10"
              inputMode="decimal"
            />
          </Field>

          <Field label="Unit" error={fieldError?.field === 'unitId' ? fieldError.message : null}>
            <Select
              value={state.unitId}
              hasError={fieldError?.field === 'unitId'}
              onChange={(e) => setState((s) => ({ ...s, unitId: e.target.value }))}
              aria-label="Unit"
            >
              <option value="">— Select a unit —</option>
              {unitOptions.map((u) => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </Select>
          </Field>

          <Field label="Purchase date" error={fieldError?.field === 'purchaseDate' ? fieldError.message : null}>
            <Input
              type="date"
              value={state.purchaseDate}
              hasError={fieldError?.field === 'purchaseDate'}
              onChange={(e) => setState((s) => ({ ...s, purchaseDate: e.target.value }))}
            />
          </Field>

          <Field label="Billed date (invoice)" error={fieldError?.field === 'billedDate' ? fieldError.message : null}>
            <Input
              type="date"
              value={state.billedDate}
              hasError={fieldError?.field === 'billedDate'}
              onChange={(e) => setState((s) => ({ ...s, billedDate: e.target.value }))}
            />
          </Field>

          <Field label="Invoice total (₹, incl. GST)" error={fieldError?.field === 'invoiceTotalText' ? fieldError.message : null}>
            <Input
              value={state.invoiceTotalText}
              hasError={fieldError?.field === 'invoiceTotalText'}
              onChange={(e) => setState((s) => ({ ...s, invoiceTotalText: e.target.value }))}
              placeholder="e.g. 1770.00"
              inputMode="decimal"
            />
          </Field>

          <SplitPreview
            invoiceTotalText={state.invoiceTotalText}
            cgstBps={currentGstCgstBps}
            sgstBps={currentGstSgstBps}
          />
        </div>

        <Field label="Notes (optional)" error={fieldError?.field === 'notes' ? fieldError.message : null}>
          <Textarea
            value={state.notes}
            rows={2}
            hasError={fieldError?.field === 'notes'}
            onChange={(e) => setState((s) => ({ ...s, notes: e.target.value }))}
            placeholder="Free-form. Invoice ref, vendor name, anything."
            maxLength={NOTES_MAX}
          />
        </Field>

        {mode === 'edit' && initial !== null
          && uploadBill !== undefined && deleteBill !== undefined && getBill !== undefined ? (
          <BillSection
            initial={initial}
            getBill={getBill}
            uploadBill={uploadBill}
            deleteBill={deleteBill}
          />
        ) : null}
      </div>
    </Modal>
  )
}

/**
 * Read-only display of the pre-GST + CGST + SGST + total-GST split
 * derived from the invoice total + the current CGST / SGST components.
 * Em-dashes when the total is empty/invalid or there's no current rate.
 */
function SplitPreview({
  invoiceTotalText,
  cgstBps,
  sgstBps
}: {
  readonly invoiceTotalText: string
  readonly cgstBps: number | null
  readonly sgstBps: number | null
}): JSX.Element {
  const cgstPct = cgstBps === null ? null : (cgstBps / 100).toFixed(2)
  const sgstPct = sgstBps === null ? null : (sgstBps / 100).toFixed(2)
  const totalPct =
    cgstBps === null || sgstBps === null ? null : ((cgstBps + sgstBps) / 100).toFixed(2)
  let preGst: number | null = null
  let cgst: number | null = null
  let sgst: number | null = null
  if (cgstBps !== null && sgstBps !== null) {
    const t = invoiceTotalText.trim()
    if (t !== '') {
      const n = Number(t)
      if (Number.isFinite(n) && n >= 0) {
        const totalPaisa = Math.round(n * 100)
        const split = splitInclusive(totalPaisa, cgstBps, sgstBps)
        preGst = split.preGstPaisa
        cgst = split.cgstPaisa
        sgst = split.sgstPaisa
      }
    }
  }
  const fmt = (p: number | null): string =>
    p === null
      ? '—'
      : `₹${(p / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  return (
    <div className="border border-border rounded-md bg-parchment/40 p-3 space-y-1 font-mono text-body-sm">
      <div className="flex justify-between">
        <span className="text-text-secondary">Pre-GST</span>
        <span>{fmt(preGst)}</span>
      </div>
      <div className="flex justify-between">
        <span className="text-text-secondary">CGST{cgstPct === null ? '' : ` @ ${cgstPct}%`}</span>
        <span>{fmt(cgst)}</span>
      </div>
      <div className="flex justify-between">
        <span className="text-text-secondary">SGST{sgstPct === null ? '' : ` @ ${sgstPct}%`}</span>
        <span>{fmt(sgst)}</span>
      </div>
      <div className="flex justify-between font-medium">
        <span className="text-text-secondary">Total GST{totalPct === null ? '' : ` @ ${totalPct}%`}</span>
        <span>{fmt(cgst === null || sgst === null ? null : cgst + sgst)}</span>
      </div>
      {cgstBps === null || sgstBps === null ? (
        <p className="text-body-xs text-terra mt-1">
          No current GST rate set. Add one on Manage &gt; GST Rates before saving.
        </p>
      ) : null}
    </div>
  )
}

function BillSection({
  initial,
  getBill,
  uploadBill,
  deleteBill
}: {
  readonly initial: ManagePurchaseDto
  readonly getBill: (id: string) => Promise<{ ok: true; data: BillDownload } | { ok: false; message: string }>
  readonly uploadBill: (id: string, input: BillUploadInput) => Promise<{ ok: true; data: ManagePurchaseDto } | { ok: false; field: string | null; message: string }>
  readonly deleteBill: (id: string) => Promise<{ ok: true; data: ManagePurchaseDto } | { ok: false; message: string }>
}): JSX.Element {
  const [billKey, setBillKey] = useState<string | null>(initial.bill_image_storage_key)
  const [busy, setBusy] = useState<'idle' | 'uploading' | 'deleting' | 'fetching'>('idle')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setBillKey(initial.bill_image_storage_key)
    setError(null)
  }, [initial.id, initial.bill_image_storage_key])

  const handleView = async (): Promise<void> => {
    setError(null)
    setBusy('fetching')
    const r = await getBill(initial.id)
    setBusy('idle')
    if (!r.ok) { setError(r.message); return }
    const bin = atob(r.data.data_base64)
    const bytes = new Uint8Array(bin.length)
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
    const blob = new Blob([bytes], { type: r.data.content_type })
    const blobUrl = URL.createObjectURL(blob)
    if (typeof window !== 'undefined') window.open(blobUrl, '_blank', 'noopener,noreferrer')
  }

  const handleFile = async (file: File): Promise<void> => {
    setError(null)
    if (!BILL_ALLOWED_TYPES.includes(file.type)) {
      setError(`Unsupported file type. Allowed: JPG, PNG, WebP, PDF.`)
      return
    }
    if (file.size > BILL_MAX_BYTES) {
      setError(`File too large. Max ${(BILL_MAX_BYTES / (1024 * 1024)).toFixed(0)} MB.`)
      return
    }
    setBusy('uploading')
    try {
      const compressed = await compressImageIfApplicable(file)
      const fileToUpload = compressed.file
      const dataBase64 = await fileToBase64(fileToUpload)
      const r = await uploadBill(initial.id, {
        filename: fileToUpload.name,
        content_type: fileToUpload.type,
        data_base64: dataBase64
      })
      setBusy('idle')
      if (!r.ok) { setError(r.message); return }
      setBillKey(r.data.bill_image_storage_key)
    } catch (e) {
      setBusy('idle')
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  const handleDelete = async (): Promise<void> => {
    if (typeof window !== 'undefined' && !window.confirm('Remove the bill image?')) return
    setError(null)
    setBusy('deleting')
    const r = await deleteBill(initial.id)
    setBusy('idle')
    if (!r.ok) { setError(r.message); return }
    setBillKey(null)
  }

  return (
    <div className="border-t border-border pt-4 mt-2 space-y-2">
      <div className="flex items-baseline justify-between">
        <span className="block text-body-sm font-body text-text-primary">Bill image</span>
        <span className="text-body-xs text-text-secondary">
          JPG / PNG / WebP / PDF · max {(BILL_MAX_BYTES / (1024 * 1024)).toFixed(0)} MB
        </span>
      </div>
      {error !== null ? <Alert tone="error">{error}</Alert> : null}

      <div className="flex flex-wrap items-center gap-2">
        {billKey === null ? (
          <span className="font-body text-body-sm text-text-secondary">No bill attached.</span>
        ) : (
          <>
            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-bamboo/20 text-charcoal font-medium text-body-xs">
              attached
            </span>
            <Button size="sm" variant="secondary" onClick={() => void handleView()} disabled={busy !== 'idle'}>
              {busy === 'fetching' ? 'Opening…' : 'View'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => void handleDelete()} disabled={busy !== 'idle'}>
              {busy === 'deleting' ? 'Removing…' : 'Remove'}
            </Button>
          </>
        )}
        <label className="inline-flex items-center">
          <input
            type="file"
            accept={BILL_ALLOWED_TYPES.join(',')}
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0]
              if (f !== undefined) void handleFile(f)
              e.target.value = ''
            }}
            disabled={busy !== 'idle'}
          />
          <span
            className={`inline-flex items-center justify-center px-3 py-1 rounded-md border border-border bg-white font-body text-body-xs cursor-pointer ${busy !== 'idle' ? 'opacity-50 cursor-not-allowed' : 'hover:border-forest/50'}`}
          >
            {busy === 'uploading'
              ? <><Spinner /> <span className="ml-2">Uploading…</span></>
              : billKey === null ? 'Choose file' : 'Replace'}
          </span>
        </label>
      </div>
    </div>
  )
}

async function fileToBase64(file: File): Promise<string> {
  const buffer = await file.arrayBuffer()
  const bytes = new Uint8Array(buffer)
  let binary = ''
  const chunkSize = 0x8000
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode.apply(
      null,
      Array.from(bytes.subarray(i, i + chunkSize))
    )
  }
  return btoa(binary)
}

function msg(e: unknown): string {
  return e instanceof Error ? e.message : String(e)
}
